import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setAuthToken, updateMyStatus } from './utils/api';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Toaster } from './components/ui/sonner';
import './App.css';

const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const TasksPage = React.lazy(() => import('./pages/TasksPage').then(m => ({ default: m.TasksPage })));
const UsersPage = React.lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const KanbanPage = React.lazy(() => import('./pages/KanbanPage').then(m => ({ default: m.KanbanPage })));
const MultiKanbanPage = React.lazy(() => import('./pages/MultiKanbanPage').then(m => ({ default: m.MultiKanbanPage })));
const ClientsPage = React.lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const CalendarApprovalsPage = React.lazy(() => import('./pages/CalendarApprovalsPage').then(m => ({ default: m.CalendarApprovalsPage })));

const ProtectedRoute = ({ children, adminOnly = false, managerOrAdmin = false, accountManagerAccess = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (managerOrAdmin && user.role !== 'admin' && user.role !== 'manager' && user.role !== 'agency_manager') {
    return <Navigate to="/" replace />;
  }

  if (accountManagerAccess && user.role !== 'account_manager' && user.role !== 'admin' && user.role !== 'agency_manager') {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, token, logout } = useAuth();

  useEffect(() => {
    if (token) {
      setAuthToken(token);

      // Update user status periodically, pausing when tab is hidden
      const statusInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          updateMyStatus().catch(() => {});
        }
      }, 60000); // Every minute

      return () => clearInterval(statusInterval);
    }
  }, [token]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (token) {
        const url = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000') + '/api/users/me/logout';
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [token]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="h-screen"><LoadingSpinner message="Loading page..." /></div>}>
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute managerOrAdmin>
                <Layout>
                  <ClientsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute managerOrAdmin>
                <Layout>
                  <CalendarApprovalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute accountManagerAccess>
                <Layout>
                  <CalendarPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/kanban"
            element={
              <ProtectedRoute>
                <Layout>
                  <KanbanPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-kanban"
            element={
              <ProtectedRoute managerOrAdmin>
                <Layout>
                  <MultiKanbanPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout>
                  <TasksPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
